import { useEffect, useReducer } from "react";
import { EntityInformationFieldCommentDto } from "../../../../data/dtos/onboarding-status.dto";
import {
  isItemResolved,
  markPotentiallyResolved,
} from "../../../../utils/updatesRequiredResolution";

/**
 * Maps a backend fieldKey to the Formik field name used in this form's three
 * cards. These fieldKeys come from the Compliance Portal's field-level-comment
 * UI (vue-control-panel's securitize-id investor details page), which keys
 * `changes.profile` by sid-investor-svc's own investor-entity property names
 * (e.g. "entityDba", "postalCode") — an arbitrary vocabulary with no shared
 * enum anywhere in the stack, and unrelated to
 * kyb.entityInformation.missingFields' naming. There's no generic prefix rule
 * that covers it (e.g. address fields carry no "address." prefix), so this is
 * a literal lookup table rather than a transform.
 *
 * TODO [ON-3654]: cp-gw now normalizes fieldKey to this exact Formik name
 * before it ever reaches sid-fe, so this table is only a fallback for
 * environments still on an older sid-gw build. Remove this mapping — and
 * make getFormikFieldName a passthrough — once that sid-gw change has been
 * out long enough that no such build remains in use.
 */
const FIELD_KEY_TO_FORMIK_NAME: Partial<Record<string, string>> = {
  name: "entityInformation.entityName",
  entityType: "entityInformation.entityType",
  incorporationDate: "entityInformation.incorporationDate",
  entityIdNumber: "entityInformation.entityIdNumber",
  entityDba: "entityInformation.businesses",
  entityWebsite: "businessInformation.website",
  entityLeiCode: "businessInformation.leiCode",
  entitySourceOfFunds: "businessInformation.sourceOfFunds",
  entityLineOfBusiness: "businessInformation.lineOfBusiness",
  street: "address.street",
  addressAdditionalInfo: "address.entrance",
  city: "address.city",
  postalCode: "address.zip",
  countryCode: "address.countryCode",
  state: "address.state",
};

export function getFormikFieldName(fieldKey: string): string {
  return FIELD_KEY_TO_FORMIK_NAME[fieldKey] ?? fieldKey;
}

/** Builds a Formik-field-name -> operator comment map from the raw backend list. */
export function getFlaggedFieldsByFormikName(
  entityInformationComments?: EntityInformationFieldCommentDto[],
): Map<string, string> {
  const flaggedFields = new Map<string, string>();

  for (const { fieldKey, comment } of entityInformationComments ?? []) {
    flaggedFields.set(getFormikFieldName(fieldKey), comment);
  }

  return flaggedFields;
}

export type FlaggedFieldState = {
  isFlagged: boolean;
  comment?: string;
};

/**
 * Resolves whether a given Formik field is currently flagged (has an
 * operator comment and hasn't been edited yet this session). `currentValue`
 * must be stringified by the caller (dates, selects, etc. all need their own
 * stable string form). `roundId` is the current requested-updates round's
 * `requestedAt` — see updatesRequiredResolution.ts for why resolution is
 * scoped to it.
 *
 * A hook (not a plain function) because detecting a fresh resolution is a
 * side effect (it writes to sessionStorage via `markPotentiallyResolved`).
 * Doing that write inline during render — as this used to — runs it on
 * every render pass, including ones React discards (StrictMode's dev-only
 * double-invoke, or an interrupted concurrent render), which can capture a
 * baseline the user never actually saw committed to the screen. The write
 * is confined to a `useEffect` (post-commit only); the render itself only
 * ever does a pure sessionStorage *read* via `isItemResolved`. Since the
 * effect's write can flip resolution after that pure read already ran, a
 * counter bump forces one extra render to pick up the change.
 */
export function useFlaggedFieldState(
  investorId: string,
  formikFieldName: string,
  currentValue: string | undefined,
  flaggedFields: Map<string, string>,
  roundId: string | undefined,
): FlaggedFieldState {
  const comment = flaggedFields.get(formikFieldName);
  const [, bumpAfterResolutionChange] = useReducer((c: number) => c + 1, 0);

  useEffect(() => {
    if (!comment) {
      return;
    }

    const wasResolved = isItemResolved(investorId, formikFieldName, roundId);
    const isResolved = markPotentiallyResolved(
      investorId,
      formikFieldName,
      currentValue ?? "",
      roundId,
    );

    if (isResolved !== wasResolved) {
      bumpAfterResolutionChange();
    }
  }, [comment, currentValue, investorId, formikFieldName, roundId]);

  if (!comment) {
    return { isFlagged: false };
  }

  return {
    isFlagged: !isItemResolved(investorId, formikFieldName, roundId),
    comment,
  };
}
